/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, combineLatest, map, of } from 'rxjs';
import { SnackbarService } from '@bcl/design-system';
import {
  ActionPriorities,
  ActionPriority,
  ActionType,
  ActionTypes,
} from './action.model';
import {
  DictionaryItem,
  DictionaryService,
  User,
} from 'src/app/services/dictionary.service';

export type ActionDictionaries = Readonly<{
  actionTypes: ActionTypes | null;
  actionPriorities: ActionPriorities | null;
  actionStatuses: DictionaryItem[] | null;
  actionResponsibilities: DictionaryItem[] | null;
  users: User[] | null;
  dateFilters: DictionaryItem[] | null;
}>;

type ActionPriorityAdditionalValues = {
  color: string;
};

type ActionTypeAdditionalValues = {
  icon: string;
  color: string;
  sendToOutlookCalendar: boolean;
  actionable: boolean;
};

@Injectable({ providedIn: 'root' })
export class ActionPointDictionariesService {
  public refreshActionList$ = new Subject<void>();

  constructor(
    private readonly dictionaryService: DictionaryService,
    private readonly snackbarService: SnackbarService
  ) {}

  public getActionDictionaries(): Observable<ActionDictionaries> {
    const sources = [
      this.getActionTypes().pipe(
        catchError(() => {
          this.errorGenericMessageAction('get');
          return of(null);
        })
      ),
      this.getActionPriorities().pipe(
        catchError(() => {
          this.errorGenericMessageAction('get');
          return of(null);
        })
      ),
      this.dictionaryService.getActionStatuses().pipe(
        catchError(() => {
          this.errorGenericMessageAction('get');
          return of(null);
        })
      ),
      this.dictionaryService.getActionResponsibilities().pipe(
        catchError(() => {
          this.errorGenericMessageAction('get');
          return of(null);
        })
      ),
      this.dictionaryService.getUsers().pipe(
        catchError(() => {
          this.errorGenericMessageAction('get');
          return of([]);
        })
      ),
      this.dictionaryService.getDateFilters().pipe(
        catchError(() => {
          this.errorGenericMessageAction('get');
          return of(null);
        })
      ),
    ];

    return combineLatest(sources).pipe(
      map(
        (x) =>
          <ActionDictionaries>{
            actionTypes: x[0].filter((type) => type.text1 === 'PC'),
            actionPriorities: x[1],
            actionStatuses: x[2],
            actionResponsibilities: x[3],
            users: x[4],
            dateFilters: x[5],
          }
      )
    );
  }

  public getActionPriorities(): Observable<ActionPriorities> {
    return this.dictionaryService.getActionPriorities().pipe(
      map((dictionaryItems) =>
        dictionaryItems.map<ActionPriority>((i) => {
          const additionalValues: ActionPriorityAdditionalValues =
            i.additionalValues
              ? <ActionPriorityAdditionalValues>JSON.parse(i.additionalValues)
              : { color: '#7f7f7f' };

          return {
            value: i.value,
            label: i.name,
            color: additionalValues.color,
          };
        })
      )
    );
  }

  public getActionTypes(): Observable<ActionTypes> {
    return this.dictionaryService.getActionTypes().pipe(
      map((dictionaryItems) =>
        dictionaryItems.map<ActionType>((i) => {
          const additionalValues: ActionTypeAdditionalValues =
            i.additionalValues
              ? <ActionTypeAdditionalValues>JSON.parse(i.additionalValues)
              : {
                  icon: '',
                  color: '#7f7f7f',
                  sendToOutlookCalendar: false,
                  actionable: false,
                };

          return {
            value: i.value,
            icon: {
              name: additionalValues.icon,
              color: additionalValues.color,
            },
            defaultTitle: i.name,
            sendToOutlookCalendar:
              additionalValues.sendToOutlookCalendar ?? false,
            actionable: additionalValues.actionable ?? false,
            text1: i.text1 ?? null,
          };
        })
      )
    );
  }

  private errorGenericMessageAction(action: 'get' | 'do'): void {
    if (action === 'do') {
      this.snackbarService.open(
        'red',
        'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_PLEASE_TRY_AGAIN_LATER'
      );
    } else {
      this.snackbarService.open(
        'red',
        'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
      );
    }
  }
}
